"use client";

import React from "react";
import { Box, Fade, Stack, Typography } from "@mui/material";
import TourHeaderButtons from "@/components/product-detail-page/tour-header/tour-header-buttons/tour-header-buttons";
import { ColletteChip } from "@collette-travel/collette-common-controls";
import { KeyInfoDetail } from "@/components/product-detail-page/tour-header/key-tour-info/types";
import { useTranslations } from "next-intl";
import KeyTourInfoColumn from "@/components/product-detail-page/tour-header/key-tour-info/column/key-tour-info.column";
import KeyTourInfoRow from "@/components/product-detail-page/tour-header/key-tour-info/row/key-tour-info.row";
import { TourHeader } from "@/types/dto/pdp/pdp-header-about";
import TourHeaderSkeleton from "@/components/product-detail-page/tour-header/tour-header.skeleton";
import ActivityLevel from "./key-tour-info/activity-level/activity-level";

interface TourHeaderProps {
  tourTitle?: string;
  tourHeaderData?: TourHeader;
  handleOpenActivityLevel: () => void;
  handleOpenTravelStyles: () => void;
  handleOpenOnTourAir: () => void;
  handleOpenRequestAQuote: () => void;
  scrollToReviews: () => void;
  isLoading: boolean;
}
const PdpTourHeader = ({
  tourTitle,
  tourHeaderData,
  handleOpenActivityLevel,
  handleOpenTravelStyles,
  handleOpenOnTourAir,
  handleOpenRequestAQuote,
  scrollToReviews,
  isLoading
}: TourHeaderProps) => {
  const t = useTranslations("PDP");

  // Show skeleton while loading or when data is not available
  if (isLoading || !tourHeaderData) {
    return <TourHeaderSkeleton tourTitle={tourTitle} />;
  }

  // Don't render section if no meaningful tour header content is available
  if (
    !tourHeaderData.days &&
    !tourHeaderData.price &&
    (!tourHeaderData.travelStyles || tourHeaderData.travelStyles.length === 0) &&
    !tourHeaderData.activityLevelNumber &&
    !tourHeaderData.activityLevelTitle
  ) {
    return null;
  }

  const tags = () => {
    if (tourHeaderData.newTour) {
      return [t("New Tour"), ...tourHeaderData.travelStyles];
    }
    return tourHeaderData.travelStyles;
  };

  const details: KeyInfoDetail[] = [
    {
      id: "days",
      label: t("DAYS"),
      value: tourHeaderData.days.toString()
    },
    {
      id: "group-size",
      label: t("GROUP SIZE"),
      value: {
        travelersTranslation: t("travelers"),
        avgNum: tourHeaderData.groupSizeAvg,
        avgTranslation: t("Avg")
      }
    },
    {
      id: "price",
      label: t("FROM PER PERSON"),
      value: {
        price: tourHeaderData.price,
        crossedOutPrice: tourHeaderData.crossedOutPrice,
        offer: tourHeaderData.offer ? t("Save up to", { offer: tourHeaderData.offer }) : undefined
      },
      ...(tourHeaderData.onTourAir
        ? {
            subLabel: t("Includes On Tour Air"),
            subLabelTooltipText: t("Includes On Tour Air Tooltip"),
            subLabelOnClick: handleOpenOnTourAir
          }
        : {})
    },
    ...(tourHeaderData?.stars !== undefined &&
    tourHeaderData?.reviewCount !== undefined &&
    tourHeaderData.reviewCount > 0
      ? [
          {
            id: "reviews",
            label: t("REVIEWS"),
            value: {
              starRating: tourHeaderData.stars ?? 0,
              totalReviews: tourHeaderData.reviewCount ?? 0
            },
            onClick: scrollToReviews
          }
        ]
      : []),
    {
      id: "activity-level",
      label: t("ACTIVITY LEVEL"),
      value: (
        <ActivityLevel
          activityLevelNumber={tourHeaderData.activityLevelNumber}
          activityLevelTitle={tourHeaderData.activityLevelTitle}
          testIdPrefix="tour-header"
        />
      ),
      tooltipText: t("Activity Level Tooltip Text"),
      onClick: handleOpenActivityLevel
    },
    {
      id: "meals",
      label: t("MEALS"),
      value: tourHeaderData.totalMeals ? `${tourHeaderData.totalMeals} Meals` : "No Meals Available"
    },
    {
      id: "travel-style",
      label: t("TRAVEL STYLE"),
      value: tourHeaderData.travelStyles.join(", "),
      tooltipText: t("Travel Style Tooltip Text"),
      onClick: handleOpenTravelStyles
    }
  ];

  return (
    <Fade in={!isLoading}>
      <Stack
        component="section"
        data-track="visibility"
        data-module-name="Tour Header"
        data-parent-container-type="pdp"
        data-ranking-algorithm="manual"
        data-module-ranking-position="2"
        sx={{
          gap: "2.4rem"
        }}
        data-testid="column-tour-header-section"
      >
        <Stack direction="row" justifyContent="space-between" gap="1.2rem" alignItems="center">
          <Stack>
            {/* Chips */}
            <Stack gap="0.8rem" marginBottom="1.2rem" direction="row">
              {tags().map((flag) => (
                <ColletteChip
                  key={`${tourHeaderData.tourId}_${flag}`}
                  color="info"
                  testId={`${tourHeaderData.tourId}_${flag}_tour-flag`}
                  sx={{
                    borderRadius: "0.8rem",
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    "& span": {
                      padding: "0 0.8rem"
                    }
                  }}
                  label={flag}
                />
              ))}
            </Stack>
            {/* H1 */}
            <Typography
              variant="h1"
              data-testid={`${tourHeaderData?.tourId}_header_title`}
              sx={{ fontSize: { xs: "2.4rem", sm: "2.6rem", md: "2.8rem" } }}
            >
              {tourHeaderData.tourTitle}
            </Typography>
            {/* H2 if subtitle */}
            {tourHeaderData.tourSubtitle && (
              <Typography
                component="h2"
                marginTop="0.4rem"
                data-testid={`${tourHeaderData?.tourId}_header_subtitle`}
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 500
                }}
              >
                {tourHeaderData.tourSubtitle}
              </Typography>
            )}
          </Stack>
          <Box sx={{ display: { xs: "none", lg: "inline" } }}>
            <TourHeaderButtons
              handleOpenRequestAQuote={handleOpenRequestAQuote}
              testId="tour-header-desktop"
            />
          </Box>
        </Stack>
        <Box
          sx={{
            display: { xs: "inline", lg: "none" },
            marginRight: "4rem"
          }}
        >
          <KeyTourInfoColumn
            labelColor="secondary"
            testId={`${tourHeaderData?.tourId}_tourinfo_column`}
            details={details}
            isLoading={isLoading}
          />
        </Box>
        <Box sx={{ display: { xs: "none", lg: "inline" } }}>
          <KeyTourInfoRow
            labelColor="secondary"
            testId={`${tourHeaderData?.tourId}_tourinfo_row`}
            details={details}
            isLoading={isLoading}
          />
        </Box>
        <Box sx={{ display: { xs: "inline", lg: "none" } }}>
          <TourHeaderButtons
            handleOpenRequestAQuote={handleOpenRequestAQuote}
            testId="tour-header-mobile"
          />
        </Box>
      </Stack>
    </Fade>
  );
};

export default PdpTourHeader;
